import { useState } from 'react';
import { GradientDropdownStrength } from '@/components/generic/dropdown/GradientDropdown';
import {
  IroScalePickerFragment,
  ScaleValue,
  useUpdateIroScaleMutation,
} from '@/graphql/generated';
import { useToast } from '@/components/layout/Toast';
import { useTranslation } from '@/hooks/useTranslation';
import { useRefetchMaterialities } from './useRefetchMaterialities';
import i18n from '@/i18n';
import { IroPickerDropdown } from '@/components/project/iro/evaluation/picker/IroPickerDropdown';

export type IroScaleItem = {
  id: ScaleValue | null;
  name: string;
  strength: GradientDropdownStrength;
};

const scaleCriteriaChoicesMap = (): Map<string | null, IroScaleItem> =>
  new Map([
    [
      ScaleValue.VeryHigh,
      {
        id: ScaleValue.VeryHigh,
        name: i18n.t(`enum:iroCriteria.scale.${ScaleValue.VeryHigh}`),
        strength: GradientDropdownStrength.VeryStrong,
      },
    ],
    [
      ScaleValue.High,
      {
        id: ScaleValue.High,
        name: i18n.t(`enum:iroCriteria.scale.${ScaleValue.High}`),
        strength: GradientDropdownStrength.Strong,
      },
    ],
    [
      ScaleValue.Medium,
      {
        id: ScaleValue.Medium,
        name: i18n.t(`enum:iroCriteria.scale.${ScaleValue.Medium}`),
        strength: GradientDropdownStrength.Medium,
      },
    ],
    [
      ScaleValue.Low,
      {
        id: ScaleValue.Low,
        name: i18n.t(`enum:iroCriteria.scale.${ScaleValue.Low}`),
        strength: GradientDropdownStrength.Light,
      },
    ],
    [
      null,
      {
        id: null,
        name: '-',
        strength: GradientDropdownStrength.VeryLight,
      },
    ],
  ]);

export const scaleCriteriaChoices = () =>
  Array.from(scaleCriteriaChoicesMap().values());

export default function IroScalePicker({
  iro,
  readOnly = false,
}: {
  iro: IroScalePickerFragment;
  readOnly?: boolean;
}) {
  const { t } = useTranslation();
  const items = scaleCriteriaChoicesMap();
  const initialScaleItem = items.get(iro.scaleCriteria?.value || null);
  const [selected, setSelected] = useState(initialScaleItem || null);
  const [update] = useUpdateIroScaleMutation();
  const toast = useToast();
  const refetchQueries = useRefetchMaterialities();

  const chooseItemId = (itemId: string | null) => {
    const item = items.get(itemId) || null;
    setSelected(item);

    update({
      variables: {
        input: {
          id: iro.id,
          scale: item?.id ?? null,
        },
      },
      refetchQueries,
    })
      .then(() => {
        toast.openToastWithMessage(
          t('toast:iro.criteria.update.success', {
            criteriaName: t('iro.criteria.scale.title'),
          }),
        );
      })
      .catch(() => {
        toast.openToastWithError(t('toast:iro.criteria.scale.update.error'));
      });
  };

  return (
    <IroPickerDropdown<IroScaleItem>
      items={items}
      selected={selected}
      chooseItemId={chooseItemId}
      assessmentStats={iro.assessmentStats?.scale ?? null}
      readOnly={readOnly}
    />
  );
}
